import { useEffect, useMemo, useRef, useState } from 'react';
import { offers, boreholes, type OfferAttachmentDto, type OfferAttachmentCategory } from '@/api/endpoints';
import { validateUploadSize, fmtBytes } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Icon } from '@/components/Icon';
import { FilePreviewModal, isPreviewable } from '@/components/ArchiveFilesCard';

/** Plik czekający na wgranie. `target`: '' (tylko klient) | `u:<id ujęcia>` | `b:<id otworu>`. */
export interface QueuedOfferFile {
  key: string;
  file: File;
  category: OfferAttachmentCategory;
  target: string;
  description: string;
}

export interface OfferIntakeRef { id: number; name: string; number?: string }

const CATEGORIES: { value: OfferAttachmentCategory; label: string }[] = [
  { value: 'INQUIRY', label: 'Zapytanie klienta (e-mail)' },
  { value: 'BOREHOLE_CARD', label: 'Karta otworu' },
  { value: 'CLIENT_ORDER', label: 'Zlecenie od klienta' },
  { value: 'CORRESPONDENCE', label: 'Korespondencja' },
  { value: 'PROTOCOL', label: 'Protokół odbioru' },
  { value: 'REPORT', label: 'Raport' },
  { value: 'OTHER', label: 'Inne' },
];

interface BoreholeOpt { id: number; designation: string; intakeId: number }

/** Kategoria z nazwy pliku — przy wrzuceniu kilku plików naraz (zapytanie + karta) trafia zwykle od razu. */
function guessCategory(name: string, hasInquiry: boolean): OfferAttachmentCategory {
  const n = name.toLowerCase();
  if (/\.(eml|msg)$/.test(n) || /zapytan|e-?mail|wiadomo/.test(n)) return 'INQUIRY';
  if (/kart[aey]|metryk|otw[oó]r|studni/.test(n)) return 'BOREHOLE_CARD';
  if (/zleceni|zam[oó]wieni/.test(n)) return 'CLIENT_ORDER';
  return hasInquiry ? 'OTHER' : 'INQUIRY';
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9ąćęłńóśźż]/g, '');

/** Otwór, którego oznaczenie jest w nazwie pliku („Karta otworu H-02-24.pdf" → H-02/24) — tylko gdy jednoznacznie. */
function boreholeFromName(fileName: string, opts: BoreholeOpt[]): BoreholeOpt | undefined {
  const n = norm(fileName);
  const hits = opts.filter(b => norm(b.designation).length >= 2 && n.includes(norm(b.designation)));
  return hits.length === 1 ? hits[0] : undefined;
}

function defaultTarget(category: OfferAttachmentCategory, fileName: string, intakes: OfferIntakeRef[],
                       opts: BoreholeOpt[], defaultBoreholeId?: number | null): string {
  if (category === 'BOREHOLE_CARD') {
    const byName = boreholeFromName(fileName, opts);
    if (byName) return `b:${byName.id}`;
    if (defaultBoreholeId && opts.some(b => b.id === defaultBoreholeId)) return `b:${defaultBoreholeId}`;
    if (opts.length === 1) return `b:${opts[0].id}`;
  }
  return intakes[0] ? `u:${intakes[0].id}` : '';
}

/** Cel spoza bieżących ujęć/otworów (plik dodany przed wyborem ujęcia, ujęcie zdjęte w kreatorze) → domyślny. */
function effectiveTarget(q: QueuedOfferFile, intakes: OfferIntakeRef[], opts: BoreholeOpt[],
                         defaultBoreholeId?: number | null): string {
  const [kind, raw] = q.target.split(':');
  const id = Number(raw);
  if (kind === 'b' && opts.some(b => b.id === id)) return q.target;
  if (kind === 'u' && intakes.some(u => u.id === id)) return q.target;
  return defaultTarget(q.category, q.file.name, intakes, opts, defaultBoreholeId);
}

/** Parametry API dla celu: otwór (wyznacza też ujęcie) albo ujęcie; '' → tylko klient. */
function targetParams(target: string) {
  const [kind, raw] = target.split(':');
  if (kind === 'b') return { boreholeId: Number(raw) };
  if (kind === 'u') return { intakeId: Number(raw) };
  return {};
}

function useBoreholeOptions(intakes: OfferIntakeRef[]): BoreholeOpt[] {
  const [opts, setOpts] = useState<BoreholeOpt[]>([]);
  const key = intakes.map(u => u.id).join(',');
  useEffect(() => {
    if (!key) { setOpts([]); return; }
    let cancelled = false;
    Promise.all(intakes.map(u => boreholes.listForIntake(u.id)
      .then(list => list.map(b => ({ id: b.id, designation: b.designation, intakeId: u.id })))
      .catch(() => [] as BoreholeOpt[])))
      .then(all => { if (!cancelled) setOpts(all.flat()); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return opts;
}

/**
 * Wgrywa kolejkę plików do zapisanej oferty (kreator: po create/update). Zwraca listę błędów
 * „plik: komunikat" — oferta już istnieje, więc błąd pliku nie może cofać zapisu.
 */
export async function uploadQueuedOfferFiles(offerId: number, queue: QueuedOfferFile[],
                                             intakes: OfferIntakeRef[],
                                             defaultBoreholeId?: number | null): Promise<string[]> {
  const errors: string[] = [];
  // Otwory potrzebne tylko do walidacji celu — pobieramy je tu, bo kreator nie trzyma listy.
  const opts = (await Promise.all(intakes.map(u => boreholes.listForIntake(u.id)
    .then(l => l.map(b => ({ id: b.id, designation: b.designation, intakeId: u.id })))
    .catch(() => [] as BoreholeOpt[])))).flat();
  for (const q of queue) {
    try {
      await offers.uploadAttachment(offerId, q.file, {
        category: q.category,
        description: q.description.trim() || undefined,
        ...targetParams(effectiveTarget(q, intakes, opts, defaultBoreholeId)),
      });
    } catch (e) {
      errors.push(`${q.file.name}: ${e instanceof Error ? e.message : String(e)}`);
    }
  }
  return errors;
}

interface Props {
  /** Zapisana oferta → lista wgranych plików. Bez niej (nowa oferta w kreatorze) — sama kolejka. */
  offerId?: number;
  intakes: OfferIntakeRef[];
  /** Otwór powiązany z ofertą — domyślny cel „Karty otworu". */
  defaultBoreholeId?: number | null;
  canEdit: boolean;
  /** Tryb kreatora: kolejkę trzyma rodzic i wgrywa ją przy zapisie oferty. Bez tego — przycisk „Wgraj". */
  queue?: QueuedOfferFile[];
  onQueueChange?: (q: QueuedOfferFile[]) => void;
  /** Węższy układ (prawa kolumna podglądu oferty). */
  compact?: boolean;
}

export function OfferAttachmentsPanel({ offerId, intakes, defaultBoreholeId, canEdit,
                                        queue: queueProp, onQueueChange, compact = false }: Props) {
  const controlled = queueProp !== undefined && onQueueChange !== undefined;
  const [ownQueue, setOwnQueue] = useState<QueuedOfferFile[]>([]);
  const queue = controlled ? queueProp : ownQueue;
  const setQueue = (q: QueuedOfferFile[]) => (controlled ? onQueueChange(q) : setOwnQueue(q));

  const [list, setList] = useState<OfferAttachmentDto[]>([]);
  const [preview, setPreview] = useState<{ id: number; name: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const opts = useBoreholeOptions(intakes);
  const multiIntake = intakes.length > 1;

  const reload = () => {
    if (offerId == null) { setList([]); return; }
    offers.attachments(offerId).then(setList).catch(e => setErr(e?.message ?? String(e)));
  };
  useEffect(reload, [offerId]);

  const targetOptions = useMemo(() => {
    const out: { value: string; label: string }[] = [];
    for (const u of intakes) {
      out.push({ value: `u:${u.id}`, label: `Ujęcie ${u.name}` });
      for (const b of opts.filter(b => b.intakeId === u.id)) {
        out.push({ value: `b:${b.id}`, label: `${multiIntake ? u.name + ' · ' : ''}otwór ${b.designation}` });
      }
    }
    return out;
  }, [intakes, opts, multiIntake]);

  const addFiles = (files: FileList | File[] | null) => {
    if (!files) return;
    const arr = Array.from(files).filter(f => f.size > 0);
    if (arr.length === 0) return;
    const sizeErr = validateUploadSize(arr);
    if (sizeErr) { setErr(sizeErr); return; }
    setErr(null);
    let hasInquiry = list.some(a => a.category === 'INQUIRY') || queue.some(q => q.category === 'INQUIRY');
    const added = arr.map((file, i) => {
      const category = guessCategory(file.name, hasInquiry);
      if (category === 'INQUIRY') hasInquiry = true;
      return {
        key: `${Date.now()}-${i}-${file.name}`, file, category,
        target: defaultTarget(category, file.name, intakes, opts, defaultBoreholeId), description: '',
      };
    });
    setQueue([...queue, ...added]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const updateQueued = (key: string, patch: Partial<QueuedOfferFile>) => {
    setQueue(queue.map(q => {
      if (q.key !== key) return q;
      const next = { ...q, ...patch };
      // Zmiana na „Kartę otworu" — od razu podpowiedz otwór, jeśli jest oczywisty.
      if (patch.category === 'BOREHOLE_CARD' && !effectiveTarget(next, intakes, opts, defaultBoreholeId).startsWith('b:')) {
        next.target = defaultTarget('BOREHOLE_CARD', next.file.name, intakes, opts, defaultBoreholeId);
      }
      return next;
    }));
  };

  const uploadNow = async () => {
    if (offerId == null || queue.length === 0) return;
    setBusy(true); setErr(null);
    const failed: QueuedOfferFile[] = [];
    const errors: string[] = [];
    for (const q of queue) {
      try {
        setUploadPct(0);
        await offers.uploadAttachment(offerId, q.file, {
          category: q.category,
          description: q.description.trim() || undefined,
          ...targetParams(effectiveTarget(q, intakes, opts, defaultBoreholeId)),
        }, p => setUploadPct(p.percent));
      } catch (e) {
        failed.push(q);
        errors.push(`${q.file.name}: ${e instanceof Error ? e.message : String(e)}`);
      }
    }
    setUploadPct(null);
    setQueue(failed);
    if (errors.length) setErr(errors.join(' · '));
    setBusy(false);
    reload();
  };

  const remove = async (a: OfferAttachmentDto) => {
    if (offerId == null) return;
    if (!confirm(`Odpiąć „${a.fileName ?? 'plik'}" od oferty? Plik zostaje w archiwum ujęcia/otworu.`)) return;
    try {
      await offers.deleteAttachment(offerId, a.id);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const whereLabel = (a: OfferAttachmentDto) =>
    a.boreholeDesignation
      ? `otwór ${a.boreholeDesignation}${a.intakeName ? ' · ' + a.intakeName : ''}`
      : a.intakeName ? `ujęcie ${a.intakeName}` : 'tylko klient';

  if (!canEdit && list.length === 0) return null;

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title">Pliki od klienta</div>
        {list.length > 0 && <span className="ml-2 text-[11px] text-ink-400">{list.length}</span>}
      </div>
      <div className={'card-body flex flex-col gap-3 ' + (dragOver ? 'bg-water-500/5' : '')}
           onDragOver={canEdit ? e => { e.preventDefault(); setDragOver(true); } : undefined}
           onDragLeave={canEdit ? () => setDragOver(false) : undefined}
           onDrop={canEdit ? e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); } : undefined}>
        {err && <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded p-2">Błąd: {err}</div>}

        {list.length > 0 && (
          <div className="flex flex-col gap-1.5">
            {list.map(a => (
              <div key={a.id} className="flex items-start gap-2 text-[12px]">
                <Icon name="file-text" className="w-3.5 h-3.5 text-ink-400 shrink-0 mt-0.5"/>
                <div className="flex-1 min-w-0">
                  <a href={a.downloadUrl} target="_blank" rel="noreferrer"
                     className="text-water-600 hover:text-water-800 truncate block" title={a.fileName ?? ''}>
                    {a.fileName ?? 'plik'}
                  </a>
                  <div className="flex flex-wrap items-center gap-1 mt-0.5">
                    <span className="pill pill-earth text-[9px]">{a.categoryName}</span>
                    <span className="text-[10px] text-ink-400">{whereLabel(a)}</span>
                  </div>
                  {a.description && <div className="text-[11px] text-ink-500 truncate" title={a.description}>{a.description}</div>}
                </div>
                {a.archiveDocumentId && a.fileName && isPreviewable(a.fileName) && (
                  <button type="button" onClick={() => setPreview({ id: a.archiveDocumentId!, name: a.fileName! })}
                          className="text-ink-400 hover:text-water-600 shrink-0" title="Podgląd">
                    <Icon name="eye" className="w-3.5 h-3.5"/>
                  </button>
                )}
                {canEdit && (
                  <button type="button" onClick={() => remove(a)} className="text-ink-400 hover:text-red-600 shrink-0"
                          title="Odepnij od oferty (plik zostaje w archiwum ujęcia/otworu)">
                    <Icon name="x" className="w-3.5 h-3.5"/>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {canEdit && queue.length > 0 && (
          <div className="flex flex-col gap-2">
            {queue.map(q => (
              <div key={q.key} className="border border-ink-200 rounded-lg p-2 flex flex-col gap-1.5">
                <div className="flex items-center gap-2 text-[12px]">
                  <Icon name="upload" className="w-3.5 h-3.5 text-ink-400 shrink-0"/>
                  <span className="font-semibold text-ink-900 truncate flex-1" title={q.file.name}>{q.file.name}</span>
                  <span className="font-mono text-[10px] text-ink-400 shrink-0">{fmtBytes(q.file.size)}</span>
                  <button type="button" onClick={() => setQueue(queue.filter(x => x.key !== q.key))}
                          className="text-ink-400 hover:text-red-600 shrink-0" title="Usuń z listy">
                    <Icon name="x" className="w-3.5 h-3.5"/>
                  </button>
                </div>
                <div className={'grid gap-1.5 ' + (compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3')}>
                  <SearchableSelect value={q.category} className="select-sm text-[11px]"
                                    onChange={v => updateQueued(q.key, { category: v as OfferAttachmentCategory })}>
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </SearchableSelect>
                  <SearchableSelect value={effectiveTarget(q, intakes, opts, defaultBoreholeId)} className="select-sm text-[11px]"
                                    disabled={intakes.length === 0}
                                    placeholder="tylko klient (brak ujęcia)"
                                    onChange={v => updateQueued(q.key, { target: v })}>
                    {intakes.length === 0 && <option value="">tylko klient (brak ujęcia)</option>}
                    {targetOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </SearchableSelect>
                  <input value={q.description} onChange={e => updateQueued(q.key, { description: e.target.value })}
                         placeholder="Opis (opcj.), np. e-mail z 14.10.2026"
                         className="input input-sm text-[11px]"/>
                </div>
              </div>
            ))}
            {controlled ? (
              <div className="text-[11px] text-ink-400">Pliki wgrają się po zapisaniu oferty.</div>
            ) : (
              <button type="button" onClick={uploadNow} disabled={busy} className="btn btn-primary btn-sm self-start">
                <Icon name="upload" className="w-3.5 h-3.5"/>
                {busy ? 'Wgrywam…' : `Wgraj (${queue.length})`}
              </button>
            )}
            {uploadPct !== null && <UploadProgressBar percent={uploadPct} label="Wysyłanie pliku…"/>}
          </div>
        )}

        {canEdit && (
          <>
            <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
                    className="border border-dashed border-ink-300 rounded-lg p-3 text-[12px] text-ink-500 hover:border-water-500 hover:text-water-700 transition text-left flex items-center gap-2">
              <Icon name="plus" className="w-4 h-4 shrink-0"/>
              <span>
                Dodaj pliki — zapytanie z e-maila (PDF), karta otworu… albo upuść je tutaj.
                {!compact && <> Trafią do archiwum ujęcia, karta otworu także do wskazanej studni, i będą widoczne w opracowaniu z tej oferty.</>}
              </span>
            </button>
            <input ref={inputRef} type="file" multiple className="hidden" onChange={e => addFiles(e.target.files)}/>
            {intakes.length === 0 && (
              <div className="text-[11px] text-amber-700">Bez ujęcia plik trafi tylko do archiwum klienta — wybierz ujęcie, żeby był też przy ujęciu i studni.</div>
            )}
          </>
        )}
      </div>
      {preview && (
        <FilePreviewModal file={preview} onClose={() => setPreview(null)}
                          files={list.filter(a => a.archiveDocumentId && a.fileName && isPreviewable(a.fileName))
                                     .map(a => ({ id: a.archiveDocumentId!, name: a.fileName! }))}/>
      )}
    </div>
  );
}
